import React, { useState, useEffect, useCallback } from "react";
import axios from 'axios';
// eslint-disable-next-line no-unused-vars
import { calculateSwimmingZones, formatTimeFromSeconds, parseTime, formatTime } from '../../utils/timeUtils';
import "./TrainingZones.css";
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const TrainingZones = ({ isTrainerView = false, athleteId = null }) => {
  const { t } = useTranslation();
  const [userId, setUserId]       = useState(null);
  const [lthr, setLthr]           = useState(174);
  const [fcMax, setFcMax]         = useState(190);
  const [fcReposo, setFcReposo]   = useState(51);
  const [ftp, setFtp]             = useState("03:43");
  const [zonesFC, setZonesFC]     = useState([]);
  const [zonesVP, setZonesVP]     = useState([]);
  const [ftpCP, setFtpCP]         = useState(220);
  const [zonesCP, setZonesCP]     = useState([]);
  const [css, setCss]             = useState("02:24");
  const [zonesSWIM, setZonesSWIM] = useState([]);
  const [cssEdited, setCssEdited] = useState(false);

  // Mapea nombres guardados en DB (siempre en español) → clave i18n
  const translateZoneName = useCallback((name) => {
    const map = {
      'Z1 Recuperación':          t('trainingZones.zoneFC.z1'),
      'Z2 Aeróbico':              t('trainingZones.zoneFC.z2'),
      'Z3 Tempo':                 t('trainingZones.zoneFC.z3'),
      'Z4 Sub-Umbral':            t('trainingZones.zoneFC.z4'),
      'Z5a Súper-Umbral':         t('trainingZones.zoneFC.z5a'),
      'Z5b Capacidad Aeróbica':   t('trainingZones.zoneFC.z5b'),
      'Z5c Capacidad anaeróbica': t('trainingZones.zoneFC.z5c'),
      'Z5 Límite Umbral':         t('trainingZones.zoneCP.z5'),
      'Z6 Capacidad Anaeróbica':  t('trainingZones.zoneCP.z6'),
      'Z7 Potencia Neuromuscular':t('trainingZones.zoneCP.z7'),
      'Z4 Umbral':                t('trainingZones.zoneSWIM.z4'),
      'Z5 Velocidad':             t('trainingZones.zoneSWIM.z5'),
      'Z6 Anaeróbica':            t('trainingZones.zoneSWIM.z6'),
    };
    return map[name] ?? name;
  }, [t]);

  const parseTimeToSeconds = (time) => {
    const [minutes, seconds] = time.split(":").map(Number);
    return minutes * 60 + seconds;
  };

  useEffect(() => {
    const resolvedUserId = isTrainerView ? athleteId : localStorage.getItem("userId");
    setUserId(resolvedUserId);
  }, [isTrainerView, athleteId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!userId) return;

    const fetchTrainingZonesFC = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/athletes/training-zones/${userId}/FC`);
        if (response.data) {
          setLthr(response.data.threshold);
          setFcMax(response.data.maxHR);
          setFcReposo(response.data.restHR);
          setZonesFC(response.data.zones.map(z => ({ ...z, name: translateZoneName(z.name) })));
        }
      } catch (error) {
        console.error("Error al obtener las zonas de FC:", error);
      }
    };

    const fetchTrainingZonesVP = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/athletes/training-zones/${userId}/VP`);
        if (response.data) {
          setFtp(formatTimeFromSeconds(response.data.threshold));
          setZonesVP(response.data.zones.map(zone => ({
            ...zone,
            name: translateZoneName(zone.name),
            min: formatTimeFromSeconds(zone.min),
            max: formatTimeFromSeconds(zone.max)
          })));
        }
      } catch (error) {
        console.error("Error al obtener las zonas de VP:", error);
      }
    };

    const fetchTrainingZonesCP = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/athletes/training-zones/${userId}/CP`);
        if (response.data) {
          setFtpCP(parseInt(response.data.threshold));
          setZonesCP(response.data.zones.map(z => ({ ...z, name: translateZoneName(z.name) })));
        }
      } catch (error) {
        console.error("Error al obtener las zonas de CP:", error);
      }
    };

    const fetchTrainingZonesSWIM = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/athletes/training-zones/${userId}/SWIM`);
        if (response.data && response.data.zones.length > 0) {
          if (!cssEdited) setCss(formatTimeFromSeconds(response.data.threshold));
          setZonesSWIM(response.data.zones.map(zone => ({
            ...zone,
            name: translateZoneName(zone.name),
            min: formatTimeFromSeconds(zone.min),
            max: formatTimeFromSeconds(zone.max)
          })));
        } else {
          const cssInSeconds = parseTime(css);
          const swimZones = calculateSwimmingZones(cssInSeconds);
          setZonesSWIM(swimZones.map(zone => ({
            ...zone,
            min: formatTimeFromSeconds(zone.min),
            max: formatTimeFromSeconds(zone.max)
          })));
          await axios.post(`${API_BASE_URL}/api/athletes/training-zones`, {
            userId, type: "SWIM", threshold: cssInSeconds, zones: swimZones
          });
        }
      } catch (error) {
        console.error("Error al obtener/guardar las zonas de SWIM:", error);
      }
    };

    fetchTrainingZonesFC();
    fetchTrainingZonesVP();
    fetchTrainingZonesCP();
    fetchTrainingZonesSWIM();
  }, [userId, css, cssEdited, translateZoneName]);

  const calculateZonesFC = () => {
    setZonesFC([
      { name: t('trainingZones.zoneFC.z1'),  min: Math.round(lthr * 0.765), max: Math.round(lthr * 0.85) },
      { name: t('trainingZones.zoneFC.z2'),  min: Math.round(lthr * 0.85),  max: Math.round(lthr * 0.89) },
      { name: t('trainingZones.zoneFC.z3'),  min: Math.round(lthr * 0.90),  max: Math.round(lthr * 0.94) },
      { name: t('trainingZones.zoneFC.z4'),  min: Math.round(lthr * 0.95),  max: Math.round(lthr * 0.99) },
      { name: t('trainingZones.zoneFC.z5a'), min: Math.round(lthr * 1.00),  max: Math.round(lthr * 1.02) },
      { name: t('trainingZones.zoneFC.z5b'), min: Math.round(lthr * 1.03),  max: Math.round(lthr * 1.06) },
      { name: t('trainingZones.zoneFC.z5c'), min: Math.round(lthr * 1.06),  max: Math.round(lthr * 1.10) }
    ]);
  };

  const calculateZonesVP = () => {
    const ftpValue = parseTime(ftp);
    setZonesVP([
      { name: t('trainingZones.zoneVP.z1'),  min: formatTime(ftpValue * 1.29), max: formatTime(ftpValue * 1.00) },
      { name: t('trainingZones.zoneVP.z2'),  min: formatTime(ftpValue * 1.14), max: formatTime(ftpValue * 1.29) },
      { name: t('trainingZones.zoneVP.z3'),  min: formatTime(ftpValue * 1.06), max: formatTime(ftpValue * 1.13) },
      { name: t('trainingZones.zoneVP.z4'),  min: formatTime(ftpValue * 0.99), max: formatTime(ftpValue * 1.05) },
      { name: t('trainingZones.zoneVP.z5a'), min: formatTime(ftpValue * 0.97), max: formatTime(ftpValue * 1.00) },
      { name: t('trainingZones.zoneVP.z5b'), min: formatTime(ftpValue * 0.90), max: formatTime(ftpValue * 0.96) },
      { name: t('trainingZones.zoneVP.z5c'), min: formatTime(ftpValue * 0.89), max: formatTime(ftpValue * 0.90) }
    ]);
  };

  const calculateZonesCP = () => {
    const ftpVal = parseFloat(ftpCP);
    if (isNaN(ftpVal) || ftpVal <= 0) { alert(t('trainingZones.invalidFtp')); return; }
    setZonesCP([
      { name: t('trainingZones.zoneCP.z1'), min: 0,                          max: Math.round(ftpVal * 0.80) },
      { name: t('trainingZones.zoneCP.z2'), min: Math.floor(ftpVal * 0.81),  max: Math.round(ftpVal * 0.89) },
      { name: t('trainingZones.zoneCP.z3'), min: Math.floor(ftpVal * 0.90),  max: Math.round(ftpVal * 0.93) },
      { name: t('trainingZones.zoneCP.z4'), min: Math.floor(ftpVal * 0.94),  max: Math.round(ftpVal * 0.99) },
      { name: t('trainingZones.zoneCP.z5'), min: Math.floor(ftpVal * 1.00),  max: Math.round(ftpVal * 1.02) },
      { name: t('trainingZones.zoneCP.z6'), min: Math.floor(ftpVal * 1.03),  max: Math.round(ftpVal * 1.06) },
      { name: t('trainingZones.zoneCP.z7'), min: Math.floor(ftpVal * 1.07),  max: 255 }
    ]);
  };

  const isValidTime = (time) => /^\d{1,2}:[0-5][0-9]$/.test(time);

  const calculateZonesSWIM = () => {
    if (!isValidTime(css)) { alert(t('trainingZones.invalidCss')); return; }
    const cssValue    = parseTime(css);
    const baseSeconds = cssValue;
    setZonesSWIM([
      { name: t('trainingZones.zoneSWIM.z1'), min: baseSeconds * 1.25, max: baseSeconds * 1.30 },
      { name: t('trainingZones.zoneSWIM.z2'), min: baseSeconds * 1.15, max: baseSeconds * 1.24 },
      { name: t('trainingZones.zoneSWIM.z3'), min: baseSeconds * 1.05, max: baseSeconds * 1.14 },
      { name: t('trainingZones.zoneSWIM.z4'), min: baseSeconds * 0.97, max: baseSeconds * 1.04 },
      { name: t('trainingZones.zoneSWIM.z5'), min: baseSeconds * 0.90, max: baseSeconds * 0.96 },
      { name: t('trainingZones.zoneSWIM.z6'), min: baseSeconds * 0.80, max: baseSeconds * 0.89 },
    ].map(zone => ({
      ...zone,
      min: formatTimeFromSeconds(zone.min),
      max: formatTimeFromSeconds(zone.max)
    })));
  };

  const saveDataFC = async () => {
    try {
      if (!userId || userId.length !== 24) { alert(t('trainingZones.invalidUserId')); return; }
      if (!zonesFC.length) { alert(t('trainingZones.noZonesFC')); return; }
      const payload = {
        userId, method: "Lactate Threshold", calculationType: "FC",
        threshold: Number(lthr), maxHR: Number(fcMax), restHR: Number(fcReposo),
        zones: zonesFC.map(z => ({ name: z.name, min: Number(z.min), max: Number(z.max) })),
        zoneType: "FC"
      };
      await axios.post(`${API_BASE_URL}/api/athletes/training-zones`, payload);
      alert(t('trainingZones.savedFC'));
    } catch (error) {
      console.error("Error al guardar los datos de FC:", error.response?.data || error.message);
      alert(t('trainingZones.saveError', { message: error.response?.data?.message || error.message }));
    }
  };

  const saveDataVP = async () => {
    try {
      if (!userId || userId.length !== 24) { alert(t('trainingZones.invalidUserId')); return; }
      if (!zonesVP.length) { alert(t('trainingZones.noZonesVP')); return; }
      const payload = {
        userId, method: "Threshold Speed", calculationType: "VP",
        threshold: parseTimeToSeconds(ftp),
        zones: zonesVP.map(zone => ({ ...zone, min: parseTimeToSeconds(zone.min), max: parseTimeToSeconds(zone.max) })),
        zoneType: "VP"
      };
      await axios.post(`${API_BASE_URL}/api/athletes/training-zones`, payload);
      alert(t('trainingZones.savedVP'));
    } catch (error) {
      console.error("Error al guardar los datos de VP:", error.response?.data || error.message);
      alert(t('trainingZones.saveError', { message: error.response?.data?.message || error.message }));
    }
  };

  const saveDataCP = async () => {
    try {
      if (!userId || userId.length !== 24) { alert(t('trainingZones.invalidUserId')); return; }
      if (!zonesCP.length) { alert(t('trainingZones.noZonesCP')); return; }
      const payload = {
        userId, method: "Joel Friel para ciclistas (7)", calculationType: "Potencia",
        threshold: Number(ftpCP),
        zones: zonesCP.map(z => ({ name: z.name, min: Number(z.min), max: Number(z.max) })),
        zoneType: "CP"
      };
      await axios.post(`${API_BASE_URL}/api/athletes/training-zones`, payload);
      alert(t('trainingZones.savedCP'));
    } catch (error) {
      console.error("❌ Error al guardar los datos de CP:", error.response?.data || error.message);
      alert(t('trainingZones.saveError', { message: error.response?.data?.message || error.message }));
    }
  };

  const saveDataSWIM = async () => {
    try {
      if (!userId || userId.length !== 24) { alert(t('trainingZones.invalidUserId')); return; }
      if (!zonesSWIM.length) { alert(t('trainingZones.noZonesSWIM')); return; }
      const payload = {
        userId, method: "Critical Swim Speed", calculationType: "SWIM",
        threshold: parseTimeToSeconds(css),
        zones: zonesSWIM.map(zone => ({ ...zone, min: parseTimeToSeconds(zone.min), max: parseTimeToSeconds(zone.max) })),
        zoneType: "SWIM"
      };
      await axios.post(`${API_BASE_URL}/api/athletes/training-zones`, payload);
      alert(t('trainingZones.savedSWIM'));
    } catch (error) {
      console.error("❌ Error al guardar los datos de SWIM:", error.response?.data || error.message);
      alert(t('trainingZones.saveError', { message: error.response?.data?.message || error.message }));
    }
  };

  return (
    <div className="training-zones-container">

      {/* ── Frecuencia Cardíaca ── */}
      <h3>{t('trainingZones.thresholdValues')}</h3>
      <div className="input-group">
        <label>{t('trainingZones.thresholdHeartRate')}</label>
        <input type="number" value={lthr}     onChange={(e) => setLthr(Number(e.target.value))} /> bpm
        <label>{t('trainingZones.maxHeartRate')}</label>
        <input type="number" value={fcMax}    onChange={(e) => setFcMax(Number(e.target.value))} /> bpm
        <label>{t('trainingZones.restingHeartRate')}</label>
        <input type="number" value={fcReposo} onChange={(e) => setFcReposo(Number(e.target.value))} /> bpm
      </div>

      <button onClick={calculateZonesFC}>{t('trainingZones.calcFC')}</button>
      <button onClick={saveDataFC}>{t('trainingZones.saveFC')}</button>

      {zonesFC.length > 0 && (
        <div>
          <h4>{t('trainingZones.thresholdBpm', { value: lthr })}</h4>
          <table>
            <thead><tr><th>{t('trainingZones.colZone')}</th><th>{t('trainingZones.colMin')}</th><th>{t('trainingZones.colMax')}</th></tr></thead>
            <tbody>
              {zonesFC.map((zone, index) => (
                <tr key={index}><td>{zone.name}</td><td>{zone.min}</td><td>{zone.max}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Velocidad / Paso ── */}
      <h3>{t('trainingZones.thresholdHR')}</h3>
      <div className="input-group">
        <label>{t('trainingZones.thresholdSpeed')}</label>
        <input type="text" value={ftp} onChange={(e) => setFtp(e.target.value)} placeholder={t('trainingZones.speedPlaceholder')} />
      </div>

      <button onClick={calculateZonesVP}>{t('trainingZones.calcVP')}</button>
      <button onClick={saveDataVP}>{t('trainingZones.saveVP')}</button>

      {zonesVP.length > 0 && (
        <div>
          <h4>{t('trainingZones.thresholdMinKm', { value: ftp })}</h4>
          <table>
            <thead><tr><th>{t('trainingZones.colZone')}</th><th>{t('trainingZones.colMin')}</th><th>{t('trainingZones.colMax')}</th></tr></thead>
            <tbody>
              {zonesVP.map((zona, index) => (
                <tr key={index}><td>{zona.name}</td><td>{zona.min}</td><td>{zona.max}</td></tr>
              ))}
            </tbody>
          </table>
          {!isTrainerView && (
            <button onClick={() => window.location.href = '/athlete-dashboard'} className="btn-back">
              {t('trainingZones.backToDashboard')}
            </button>
          )}
        </div>
      )}

      {/* ── Potencia (CP) ── */}
      <h3>{t('trainingZones.thresholdPower')}</h3>
      <div className="input-group">
        <label>{t('trainingZones.ftpWatts')}</label>
        <input type="number" value={ftpCP} onChange={(e) => setFtpCP(Number(e.target.value))} placeholder={t('trainingZones.ftpPlaceholder')} />
      </div>

      <button onClick={calculateZonesCP}>{t('trainingZones.calcCP')}</button>
      <button onClick={saveDataCP}>{t('trainingZones.saveCP')}</button>

      {zonesCP.length > 0 && (
        <div>
          <h4>{t('trainingZones.thresholdWatts', { value: ftpCP })}</h4>
          <table>
            <thead><tr><th>{t('trainingZones.colZone')}</th><th>{t('trainingZones.colMin')}</th><th>{t('trainingZones.colMax')}</th></tr></thead>
            <tbody>
              {zonesCP.map((zone, index) => (
                <tr key={index}><td>{zone.name}</td><td>{zone.min} w</td><td>{zone.max} w</td></tr>
              ))}
            </tbody>
          </table>
          {!isTrainerView && (
            <button onClick={() => window.location.href = '/athlete-dashboard'} className="btn-back">
              {t('trainingZones.backToDashboard')}
            </button>
          )}
        </div>
      )}

      {/* ── CSS Natación ── */}
      <h3>{t('trainingZones.thresholdSwim')}</h3>
      <div className="input-group">
        <label>{t('trainingZones.cssLabel')}</label>
        <input
          type="text"
          value={css}
          onChange={(e) => { setCss(e.target.value); setCssEdited(true); }}
          placeholder={t('trainingZones.cssPlaceholder')}
        />
      </div>

      <button onClick={calculateZonesSWIM}>{t('trainingZones.calcSWIM')}</button>
      <button onClick={saveDataSWIM}>{t('trainingZones.saveSWIM')}</button>

      {zonesSWIM.length > 0 && (
        <div>
          <h4>{t('trainingZones.thresholdMinPer100m', { value: css })}</h4>
          <table>
            <thead><tr><th>{t('trainingZones.colZone')}</th><th>{t('trainingZones.colMin')}</th><th>{t('trainingZones.colMax')}</th></tr></thead>
            <tbody>
              {zonesSWIM.map((zone, index) => (
                <tr key={index}><td>{zone.name}</td><td>{zone.min}</td><td>{zone.max}</td></tr>
              ))}
            </tbody>
          </table>
          {!isTrainerView && (
            <button onClick={() => window.location.href = '/athlete-dashboard'} className="btn-back">
              {t('trainingZones.backToDashboard')}
            </button>
          )}
        </div>
      )}

    </div>
  );
};

export default TrainingZones;
